import { useContext } from "react";
import { createBrowserRouter, Navigate, useParams } from "react-router-dom";

import App from "@/App";
import NewDesignLayout from "@/components/NewDesign/NewDesignLayout.tsx";
import RedirectNotification from "@/components/RedirectNotification";
import RouteErrorFallback from "@/components/RouteErrorFallback";
import { ampli, client, config, flags } from "@/config";
import { AppContext } from "@/models/AppStateProvider";
import AuthSuccess from "@/routes/authSuccess";
import SubPage from "@/routes/workspace/SubPage.tsx";
import DirectAuth from "./directAuth";
import Onboarding from "./onboarding";
import Root from "./root";
import Workspace from "./workspace";
import DebugItemId from "./workspace/debugItem";
import FeedId from "./workspace/feedId";
import LoadingWorkspace from "./workspace/LoadingWorkspace";
import MaintenanceMode from "./workspace/MaintenanceMode";
import TemplateManagement from "./workspace/templateManagement";
import Workflows from "./workspace/workflows";
import WorkspaceId from "./workspace/workspaceId";
import WorkspaceUserManagement from "./workspace/workspaceUserManagement";

function DesignLayoutSwitcher() {
  const { flags } = useContext(AppContext);
  const newDesign = flags.newDesign as boolean;

  return newDesign ? <NewDesignLayout /> : <Workspace />;
}

function NewDesignSubPageGuard() {
  const { flags } = useContext(AppContext);
  const { workspaceId } = useParams();
  const newDesign = flags.newDesign as boolean;

  if (newDesign === false) {
    return <Navigate to={`/workspaces/${workspaceId}`} replace />;
  }

  return (
    <NewDesignLayout>
      <SubPage />
    </NewDesignLayout>
  );
}

const routes = [
  {
    element: <App config={config} flags={flags} client={client} ampli={ampli} />,
    errorElement: <RouteErrorFallback />,
    children: [
      {
        path: "/",
        element: <Root />,
      },
      {
        path: "/workspaces/:workspaceId/:subPage",
        element: <NewDesignSubPageGuard />,
      },
      {
        path: "/workspaces",
        element: <DesignLayoutSwitcher />,
        children: [
          {
            path: "/workspaces/:workspaceId",
            element: <WorkspaceId />,
          },
          {
            path: "/workspaces/:workspaceId/loading-workspace",
            element: <LoadingWorkspace />,
          },
          {
            path: "/workspaces/:workspaceId/drivers/:workspaceMembershipId/channel/:feedId",
            element: <FeedId type="drivers" />,
          },
          {
            path: "/workspaces/:workspaceId/:type/:feedId",
            element: <FeedId type="legacy" />,
          },
          {
            path: "/workspaces/:workspaceId/feeds/:feedId/items/:itemId/debug",
            element: <DebugItemId />,
          },
          {
            path: "/workspaces/:workspaceId/user-management",
            element: <WorkspaceUserManagement />,
          },
          {
            path: "/workspaces/:workspaceId/feeds/:feedId/tools/scheduled-messages",
            element: <Workflows />,
          },
          {
            path: "/workspaces/:workspaceId/feeds/:feedId/tools/template-management",
            element: <TemplateManagement />,
          },
        ],
      },
      {
        path: "/onboarding",
        element: <Onboarding />,
      },
      {
        path: "/authSuccess",
        element: <AuthSuccess />,
      },
      {
        path: "/notification-redirect",
        element: <RedirectNotification />,
      },
      {
        path: "/directAuth",
        element: <DirectAuth />,
      },
      {
        path: "/maintenance-mode",
        element: <MaintenanceMode />,
      },
    ],
  },
];
export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter(routes);
